import { RenderingSettingsContext } from "@/components/common/rendering-settings-context";
import { SceneEventsContextProvider } from "@/components/common/scene-events-context";
import { SettingsContext } from "@/components/common/settings-context/settings-context";
import { useThreeContext } from "@/components/common/three-context/three-context";
import { useViewRuntimeContext } from "@/components/common/view-runtime-context";
import { AnalysisRenderer } from "@/components/r3f/renderers/analyses/analysis-renderer";
import { ColormapScale } from "@/components/r3f/renderers/analyses/colormap-scale";
import { SuspenseLoadNotifier } from "@/components/r3f/utils/suspense-load-notifier";
import { ModeDataContext } from "@/modes/mode-data-context";
import { PointCloudObject } from "@/object-cache";
import { PointCloudAnalysis } from "@/store/point-cloud-analysis-tool-slice";
import { isPerspectiveCamera } from "@/types/threejs-type-guards";
import { DEFAULT_CANVAS_OPTIONS, grey } from "@faro-lotv/app-component-toolbox";
import { AuthContext } from "@faro-lotv/gate-keepers";
import { assert, LotvRenderer, SupportedCamera } from "@faro-lotv/lotv";
import { CAMERA_SYNC_PRIORITY } from "@faro-lotv/spatial-ui";
import { Box } from "@mui/system";
import {
  OrthographicCamera,
  PerformanceMonitor,
  PerspectiveCamera,
  useContextBridge,
} from "@react-three/drei";
import { createRoot, useFrame, useThree } from "@react-three/fiber";
import { useEffect, useMemo, useState, type JSX } from "react";
import { ReactReduxContext } from "react-redux";
import { CanvasTexture, Color, Texture } from "three";

type RootType = ReturnType<typeof createRoot>;

// Props for the AnalysisReportScene component
type PdfReportSceneProps = {
  /** The point cloud object to render in the scene */
  pointCloud: PointCloudObject;
  /**  analysis data to overlay on the point cloud */
  analysis: PointCloudAnalysis;
  /** Max width in pixels for preview */
  sceneMaxWidth: number;
  /** Max height in pixels for preview */
  sceneMaxHeight: number;
};

/**
 * Renders a 3D analysis scene for PDF reports using a separate React Three Fiber root.
 * This component creates an off-screen canvas and renders the 3D scene into it,
 * including the point cloud and analysis overlays. It also displays a colormap scale
 * for the analysis results.
 *
 * @param pointCloud - The point cloud data to be visualized in the scene.
 * @param analysis - The analysis data to be visualized and used for the colormap scale.
 * @returns A JSX element containing the rendered 3D scene and colormap scale overlay.
 */
export function AnalysisReportScene({
  pointCloud,
  analysis,
  sceneMaxWidth,
  sceneMaxHeight,
}: PdfReportSceneProps): JSX.Element {
  const { renderer } = useThreeContext();
  assert(!!renderer, "Three renderer must be initialized.");

  const { cameras } = useViewRuntimeContext();

  // Canvas where we will render the scene to a texture
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
  // The root rendered to this canvas
  const [root, setRoot] = useState<RootType>();

  // Initializes a separate React Three root for this canvas
  useEffect(() => {
    if (!canvas) return;

    // WARNING: On Firefox, this might produce a warning "R3F.createRoot should only be called once!"
    // due to a fast unmount and remount in dev. It's harmless in production.

    const r = createRoot(canvas);
    setRoot(r);

    return () => {
      r.unmount();
    };
  }, [canvas]);

  // Configure the root once it's initialized
  useEffect(() => {
    if (!root) return;

    root.configure({
      // We need color management to match main canvas
      ...DEFAULT_CANVAS_OPTIONS,
      frameloop: "demand",
      gl: ({ canvas }) =>
        new LotvRenderer({
          canvas,
          premultipliedAlpha: false,
          preserveDrawingBuffer: true,
        }),
    });
  }, [root, renderer.domElement]);

  // Bridge context from parent to this root
  const Bridge = useContextBridge(
    AuthContext,
    ReactReduxContext,
    RenderingSettingsContext,
    ModeDataContext,
    SettingsContext,
  );

  const screenRatio = renderer.domElement.width / renderer.domElement.height;
  // Height should be 25% of scene, but no more than 512 pixels to avoid oversized capture
  const candidateHeight = Math.min(
    sceneMaxHeight,
    renderer.domElement.height / 4,
  );
  // Width should be proportional to height based on the renderer's aspect ratio, but no more than 512 pixels
  const width = Math.min(candidateHeight * screenRatio, sceneMaxWidth);
  // Fix the final height in case the width was clipped by  sceneMaxWidth
  const height = width / screenRatio;
  const htmlScale = width / renderer.domElement.width;

  // Once root is initialized, we render AnalysisReport3DView into it
  useEffect(() => {
    if (!root) return;

    root.render(
      <Bridge>
        <PerformanceMonitor>
          <SuspenseLoadNotifier>
            <SceneEventsContextProvider>
              <AnalysisReport3DView
                pointCloud={pointCloud}
                analysis={analysis}
                currentCamera={cameras[0]}
                htmlScale={htmlScale}
              />
            </SceneEventsContextProvider>
          </SuspenseLoadNotifier>
        </PerformanceMonitor>
      </Bridge>,
    );
  }, [root, Bridge, pointCloud, analysis, cameras, htmlScale]);

  return (
    <Box
      component="div"
      style={{
        height: `${height}px`,
        maxWidth: `${width}px`,
        aspectRatio: renderer.domElement.width / renderer.domElement.height,
        backgroundColor: "transparent",
        position: "relative",
        marginBottom: "1rem",
      }}
    >
      <Box
        component="div"
        style={{
          height: `${height}px`,
          maxWidth: "100%",
          aspectRatio: renderer.domElement.width / renderer.domElement.height,
          position: "absolute",
          backgroundColor: "transparent",
        }}
      >
        {/* Canvas for rendering 3D scene */}
        <canvas ref={setCanvas} style={{ width: "100%", height: "100%" }} />
        {/* Display colormap scale in the corner */}
        <Box
          component="div"
          style={{
            position: "absolute",
            visibility: "visible",
            padding: "10px",
            borderRadius: "8px",
            backgroundColor: `${grey[0]}90`,
            top: `${height / 2}px`,
            right: 0,
            transform: `scale(${htmlScale}) translate(-50%, -50%)`,
            transformOrigin: "right top",
            zIndex: 10,
          }}
        >
          <ColormapScale analysis={analysis} />
        </Box>
      </Box>
    </Box>
  );
}

// Props for the AnalysisReport3DView component
type AnalysisReport3DViewProps = Pick<
  PdfReportSceneProps,
  "pointCloud" | "analysis"
> & {
  /** The camera from the main viewer to sync with  */
  currentCamera: SupportedCamera;
  /** Scale to apply to HTML components */
  htmlScale: number;
};

/**
 * Renders the 3D scene for the PDF analysis report.
 * Synchronizes the camera with the main viewer and overlays the analysis results.
 *
 * @returns A JSX element containing the 3D scene and analysis overlays.
 */
function AnalysisReport3DView({
  pointCloud,
  analysis,
  currentCamera,
  htmlScale,
}: AnalysisReport3DViewProps): JSX.Element {
  // Access the R3F camera and scene objects
  const { camera, scene } = useThree();

  const { renderer } = useThreeContext();

  // Prepare a texture from the main renderer's canvas to reuse as background
  const texture: Texture | Color = useMemo(
    () =>
      renderer?.domElement
        ? new CanvasTexture(renderer.domElement)
        : new Color(grey[100]),
    [renderer?.domElement],
  );

  // Update this texture each frame and sync camera
  useFrame(() => {
    if (texture instanceof Texture) {
      texture.needsUpdate = true;
    }
    if (scene.background !== texture) {
      scene.background = texture;
    }

    // Perspective → Perspective
    if (isPerspectiveCamera(camera) && isPerspectiveCamera(currentCamera)) {
      camera.copy(currentCamera);
      camera.updateProjectionMatrix();
      return;
    }

    // Orthographic → Orthographic
    if (!isPerspectiveCamera(camera) && !isPerspectiveCamera(currentCamera)) {
      camera.copy(currentCamera);
      camera.updateProjectionMatrix();
    }
  }, CAMERA_SYNC_PRIORITY);

  return (
    <>
      {/* Mount the matching camera type as default */}
      {isPerspectiveCamera(currentCamera) ? (
        <PerspectiveCamera makeDefault />
      ) : (
        <OrthographicCamera makeDefault />
      )}

      {/* If we have an analysis, we show the colormap renderer on the pointcloud */}
      <AnalysisRenderer
        pointCloud={pointCloud}
        analysis={analysis}
        labelsScale={htmlScale}
      />
    </>
  );
}
